import React, { createContext, useContext, useEffect, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Modal, SpaceBetween } from "@amzn/awsui-components-react";

const DirtyContext = createContext<(dirty: boolean) => void>(() => undefined);
export const useOmsNavigationGuard = () => useContext(DirtyContext);

function context(path: string) {
  const match = path.match(
    /^\/dashboard\/oms\/(channels|addon|tvod)(?:\/account\/(\d{5}))?/
  );
  return match ? `${match[1]}:${match[2] || "legacy"}` : undefined;
}
function workflow(path: string) {
  return /^\/dashboard\/oms\/.+\/(export|presets)(?:\/|$)/.test(path);
}

export default function OMSNavigationGuard({
  children,
}: {
  children: React.ReactElement<{ location?: Location }>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [committed, setCommitted] = useState(location);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<Location>();
  useEffect(() => {
    if (committed.key === location.key) return;
    if (
      committed.pathname === location.pathname &&
      committed.search === location.search &&
      committed.hash === location.hash
    ) {
      // Keeping an explicit preset edit replaces the attempted history entry.
      // That restoration is not another request to leave the dirty editor.
      setCommitted(location);
      return;
    }
    const internal =
      context(committed.pathname) === context(location.pathname) &&
      workflow(committed.pathname) &&
      workflow(location.pathname) &&
      !committed.pathname.endsWith("/edit") &&
      !committed.pathname.endsWith("/copy");
    if (dirty && !internal) {
      setPending(location);
      return;
    }
    setCommitted(location);
    if (!internal) setDirty(false);
  }, [location, committed, dirty]);
  useEffect(() => {
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        // Native browser confirmation requires returnValue for older browsers.
        // eslint-disable-next-line no-param-reassign
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", unload);
    return () => window.removeEventListener("beforeunload", unload);
  }, [dirty]);
  const keep = () => {
    setPending(undefined);
    navigate(committed.pathname + committed.search, { replace: true });
  };
  return (
    <DirtyContext.Provider value={setDirty}>
      {React.cloneElement(children, { location: committed })}
      {pending && (
        <Modal
          visible
          size="medium"
          header="Discard changes?"
          closeAriaLabel="Close discard confirmation"
          onDismiss={keep}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={keep}>Keep editing</Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setDirty(false);
                    setCommitted(pending);
                    setPending(undefined);
                  }}
                >
                  Discard changes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Your unsaved settings will be discarded. Accepted exports continue
          preparing.
        </Modal>
      )}
    </DirtyContext.Provider>
  );
}
